import { useContext, useEffect, useRef, useState } from "react";
import { Button, Form, Input, Row, Col, Spin, Select } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { FormRow } from "../../../../components/form/form-row";
import {
  TContext,
  TDropdownOptions,
  TResponseModel,
} from "../../../../types/config";
import { eHTTPStatusCode, eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { TDefectLogBook } from "../../../../types";
import {
  GetCustomerList,
  UploadDefectLogBook,
} from "../../../../utils/api.constant";
import { UserContext } from "../../../../context";
import "../../../../index.css";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import {
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import Utils from "../../../../utils";
import AuthUtil from "../../../../utils/auth";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

export default function DefectLogBookForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [file, setFile] = useState<File | null>(null); // Store the uploaded file
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Ref to handle file input
  const [selectedName, setSelectedName] = useState<string>("");
  const context: TContext = useContext(UserContext);

  const { post } = useFetch();
  const { onShowToast } = useToast();

  useEffect(() => {
    fetchCustomerOptions();
  }, []);

  const fetchCustomerOptions = async () => {
    try {
      const payload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };
      setIsLoading(true);
      const response: TResponseModel = await post(GetCustomerList, payload);
      if (response) {
        const { data, dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setCustomerOptions(Utils.mapDropdownOptions(data));
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!form.getFieldValue("customerId")) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Please fill all the mandatory fields",
      });
      return;
    }
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      const isPdf =
        selectedFile.type === "application/pdf" ||
        selectedFile.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: "Please upload a PDF file!",
        });
        setFile(null); // Reset file state
        event.target.value = ""; // Clear the input
      } else {
        setFile(selectedFile); // Set the selected file
        // handleSubmit(selectedFile);
      }
    }

    // Reset the input value to ensure `onChange` is triggered for the same file
    event.target.value = "";
  };

  const handleSubmit = async () => {
    if (!file) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Please select a defect code book file!",
      });
      return;
    }

    // Implement your file upload logic here
    const formData = new FormData();
    formData.append("Data.File", file);
    formData.append("Data.CustomerId", form.getFieldValue("customerId"));

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${UploadDefectLogBook}`,
        {
          method: "POST",
          body: formData,

          headers: {
            ...AuthUtil.getToken(),
          },
        }
      );
      if (response.status === eHTTPStatusCode.OK) {
        const responseData = (await response.json()) as TResponseModel;
        if (responseData.dataResponse.returnCode === eResultCode.SUCCESS) {
          onShowToast({
            type: ToastType.success,
            title: <CheckOutlined />,
            position: ToastOpen.leftBottom,
            content: responseData.dataResponse.description,
          });
          props.onRefreshList();
          props.onCloseDrawer();
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.leftBottom,
            content: responseData.dataResponse.description,
          });
        }
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Error uploading file",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const contentStyle: React.CSSProperties = {
    padding: 50,
  };

  const content = <div style={contentStyle} />;

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={handleSubmit}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <Row
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Col>
              <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
                Upload Defect Code Book
              </h3>
            </Col>
            <Col>
              <Button
                type="default"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => {
                  props.onCloseDrawer();
                }}
              >
                Close
              </Button>
            </Col>
          </Row>
        </FormHeader>
        <FormContent>
          {" "}
          <Row
            gutter={16}
            className="formRowHeight"
            style={{ marginTop: "10px" }}
          >
            <Col span={24}>
              <Form.Item
                name="customerId"
                label="Customer"
                rules={[{ required: true, message: "Please choose customer" }]}
              >
                <Select
                  size="large"
                  disabled={isLoading}
                  showSearch
                  placeholder="Select a customer"
                  options={customerOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value, option) => {
                    const selectedOption = option as {
                      label: string;
                      value: string;
                    };
                    setSelectedName(selectedOption.label);
                  }}
                ></Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            {isLoading ? (
              <Col
                span={24}
                style={{
                  justifyContent: "center",
                  display: "flex",
                  marginTop: "1rem",
                  flexDirection: "column",
                }}
              >
                <Spin
                  tip={
                    <span
                      style={{
                        fontFamily: "Montserrat-Regular",
                      }}
                    >
                      Validating . . .
                    </span>
                  }
                  size="large"
                >
                  {content}
                </Spin>
              </Col>
            ) : (
              <Col
                span={24}
                style={{
                  justifyContent: "flex-start",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Form.Item label="" style={{ marginBottom: 0 }}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    style={{ display: "none" }} // Hide the input
                  />
                  <Button
                    disabled={isLoading || !form.getFieldValue("customerId")}
                    onClick={triggerFileInput}
                    loading={isLoading}
                    icon={<UploadOutlined />}
                    size="large"
                    style={{
                      width: "100%",
                      textOverflow: "ellipsis", // Truncate text
                      overflow: "hidden", // Hide overflow content
                      whiteSpace: "nowrap", // Prevent wrapping to a new line
                    }}
                  >
                    {file ? (
                      <span
                        style={{
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.name}
                      </span>
                    ) : (
                      "Choose to Upload"
                    )}
                  </Button>
                </Form.Item>
              </Col>
            )}
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              size="large"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              loading={isLoading}
              className="btnDimensions"
            >
              {props.id ? "Update" : "Save"}
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
